// One OT case, from booking to closure.
//
// The tabs follow the patient, not the database: Overview, Consent, Pre-op,
// Flow, Theatre, Notes, Recovery, Billing. A tab is shown greyed rather than
// hidden once a case has passed it, because a closed case's theatre record is
// exactly what someone comes back to read.
//
// THE "ADVANCE TO" BUTTONS ARE THE SERVER'S. `allowed_transitions` comes down on
// every case from the same transition map the backend enforces (ADR-0003), so
// the UI can never offer a move that will be refused. Nothing here decides what
// is legal.

import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  IPD_COLORS,
  IpdFormField,
  IpdSectionCard,
  IpdTabBar,
  IpdTextField,
} from "../../ipd/utils";
import { formatIstDateTime, formatIstTime } from "../../../utils/istDateTime";
import { fetchCase, fetchLifecycle, moveLifecycle } from "../api/otApi";
import OtScreen from "../components/OtScreen";
import {
  DisabledHint,
  FirmnessChip,
  LifecycleChip,
  OtSelect,
  TrackingChip,
  errorMessage,
  report,
} from "../components/common";
import {
  CATEGORY_REQUIRED_FOR,
  LIFECYCLE_LABELS,
  OT_BASE,
  REASON_REQUIRED_FOR,
} from "../constants";

import OverviewPanel from "../components/panels/OverviewPanel";
import PreopPanel from "../components/panels/PreopPanel";
import FlowPanel from "../components/panels/FlowPanel";
import TheatrePanel from "../components/panels/TheatrePanel";
import NotesPanel from "../components/panels/NotesPanel";
import RecoveryPanel from "../components/panels/RecoveryPanel";
import BillingPanel from "../components/panels/BillingPanel";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "preop", label: "Pre-op" },
  { id: "flow", label: "Patient Flow" },
  { id: "theatre", label: "Theatre" },
  { id: "notes", label: "Notes" },
  { id: "recovery", label: "Recovery" },
  { id: "billing", label: "Billing" },
];

/**
 * Stopping a case: a reason, and for a cancellation a coded category too.
 *
 * The category is the whole point. A free-text reason answers "why did this one
 * not happen"; only a coded one answers "why do ours not happen", and the second
 * is the question that changes how a theatre runs. The options are the server's
 * — fetched, not listed here — so this form can never offer a value the POST
 * will refuse.
 *
 * "Could it have been avoided" is asked separately rather than inferred from the
 * category, because it is a judgement about THIS case: a facility-resource
 * cancellation may or may not have been preventable, and guessing would inflate
 * the one number the taxonomy exists to make honest. Not answering is allowed
 * and means exactly that — it is not a "no".
 */
function StopCaseDialog({ open, target, categories, onConfirm, onCancel }) {
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("");
  const [avoidable, setAvoidable] = useState("");

  const needsCategory = CATEGORY_REQUIRED_FOR.includes(target);
  const verb = LIFECYCLE_LABELS[target] || target;

  useEffect(() => {
    if (open) {
      setReason("");
      setCategory("");
      setAvoidable("");
    }
  }, [open]);

  // Choosing a category pre-fills the avoidability the way that category usually
  // goes, as a starting point the person cancelling can overrule. A default the
  // user never sees is a default nobody chose, so it is shown as a selection
  // rather than sent invisibly.
  const chooseCategory = (value) => {
    setCategory(value);
    const chosen = categories.find((c) => c.value === value);
    if (!chosen || chosen.avoidable_default === null || chosen.avoidable_default === undefined) {
      setAvoidable("");
    } else {
      setAvoidable(chosen.avoidable_default ? "yes" : "no");
    }
  };

  const incomplete = !reason.trim() || (needsCategory && !category);

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>{`${verb} Surgical Case`}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
          {needsCategory
            ? "Documenting cancellation justification and classification is mandatory for clinical audit, surgical governance, and NABH/JCI quality metrics."
            : "Documenting clinical deferral/postponement justification is required for clinical governance and scheduling audit."}
        </Typography>

        <Box sx={{ display: "grid", gap: 2 }}>
          {categories.length > 0 && (
            <IpdFormField label={needsCategory ? "Category" : "Category (optional)"}>
              <OtSelect
                value={category}
                onChange={(e) => chooseCategory(e.target.value)}
                options={categories.map((c) => ({ value: c.value, label: c.label }))}
                placeholder="Choose a category"
              />
            </IpdFormField>
          )}

          <IpdFormField label="Clinical Cancellation Reason / Detailed Narrative *">
            <IpdTextField
              fullWidth
              multiline
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </IpdFormField>

          <IpdFormField label="Avoidability Audit Classification">
            <OtSelect
              value={avoidable}
              onChange={(e) => setAvoidable(e.target.value)}
              options={[
                { value: "yes", label: "Avoidable / Preventable (Hospital / Operational / Booking)" },
                { value: "no", label: "Unavoidable (Acute Patient Condition / Medical Contraindication)" },
              ]}
              placeholder="Select classification (optional audit assessment)..."
            />
          </IpdFormField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Dismiss / Keep Case Active</Button>
        <DisabledHint
          reason={
            !reason.trim()
              ? "Enter a reason — the server records one against every case taken off the list."
              : needsCategory && !category
              ? "Select a category so this case is counted correctly in the cancellation register."
              : ""
          }
        >
          <Button
            variant="contained"
            color="error"
            disabled={incomplete}
            onClick={() =>
              onConfirm({
                reason: reason.trim(),
                cancel_category: category || undefined,
                // Left out entirely when unanswered: the server stores null, which
                // reads as "nobody judged" rather than as "not avoidable".
                cancel_avoidable: avoidable === "" ? undefined : avoidable === "yes",
              })
            }
          >
            {verb}
          </Button>
        </DisabledHint>
      </DialogActions>
    </Dialog>
  );
}



/** A safety flag that has to be seen, not found. */
function AlertBand({ label, detail }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        gap: 1,
        px: 1.5,
        py: 0.9,
        borderRadius: 1,
        backgroundColor: IPD_COLORS.error.bg,
        border: `1px solid ${IPD_COLORS.error.border}`,
        minWidth: 220,
        flex: "1 1 240px",
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 800, color: IPD_COLORS.error.text, letterSpacing: "0.04em" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.error.text }}>{detail}</Typography>
    </Box>
  );
}

/**
 * The case header.
 *
 * Ordered by what someone opening a case actually needs to know, which is not
 * the order the fields are stored in: anything that could hurt the patient
 * first, then the state of the case, then WHAT OPERATION, WHERE and WHEN — the
 * three facts that used to be a tab away — then identity, then what can be done
 * next.
 *
 * The safety flags get their own band rather than a chip in the row. A
 * bio-hazard warning sitting between "Draft" and "Provisional" is a warning
 * competing with two labels nobody needs to act on.
 */
export function CaseHeader({ otCase, allowed, onAdvance }) {
  const booking = (otCase.bookings || []).find((b) => b.status === "ACTIVE");
  const primary = (otCase.procedures || []).find((p) => p.is_primary);
  const stopMoves = allowed.filter((to) => REASON_REQUIRED_FOR.includes(to));
  const goMoves = allowed.filter((to) => !REASON_REQUIRED_FOR.includes(to));

  const where = [
    booking?.operating_room_name,
    booking && `${formatIstDateTime(booking.start_datetime)}–${formatIstTime(booking.end_datetime)}`,
    otCase.primary_surgeon_name,
    otCase.anaesthetist_name,
  ].filter(Boolean);

  const who = [
    otCase.health_record_id && `MRN ${otCase.health_record_id}`,
    (otCase.age ?? null) !== null && `${otCase.age} / ${otCase.gender || "—"}`,
    `${otCase.case_source} · ${otCase.scheduling_type}`,
    otCase.admission_id ? `Inpatient Admission #${otCase.admission_id}` : "Outpatient / Day Care Case (No Inpatient Admission Linked)",
    otCase.reschedule_count > 0 && `Rescheduled ${otCase.reschedule_count}×`,
  ].filter(Boolean);

  return (
    <IpdSectionCard sx={{ mb: 2 }}>
      {(otCase.biohazard || otCase.known_allergies) && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          {otCase.biohazard && (
            <AlertBand label="BIO-HAZARD" detail={otCase.biohazard_note || "Universal precautions"} />
          )}
          {otCase.known_allergies && (
            <AlertBand label="ALLERGY" detail={otCase.known_allergies} />
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
        <LifecycleChip status={otCase.lifecycle_status} />
        <FirmnessChip firmness={otCase.firmness} />
        <TrackingChip status={otCase.tracking_status} />
      </Box>

      <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>
        {primary?.procedure_name || otCase.diagnosis || "Surgical Procedure Not Specified"}
      </Typography>
      {where.length > 0 && (
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mt: 0.4 }}>
          {where.join("  ·  ")}
        </Typography>
      )}
      <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textMuted, mt: 0.6 }}>
        {who.join("   ")}
      </Typography>

      {otCase.list_remarks && (
        <Typography sx={{ fontSize: 12.5, mt: 1, color: IPD_COLORS.warning.text }}>
          {otCase.list_remarks}
        </Typography>
      )}

      {/* Said before the button is pressed rather than after it is refused.
          Only once closing is actually the next move — on a case still in
          pre-op this is a list of things nobody is late with yet. */}
      {allowed.includes("CLOSED") && (otCase.notes_outstanding || []).length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: IPD_COLORS.warning.bg,
            color: IPD_COLORS.warning.text,
            fontSize: 13,
          }}
        >
          Case closure blocked: Incomplete mandatory documentation. The following clinical sections require completion prior to closure:
          {(otCase.notes_outstanding || []).map((item) => (
            <Box key={item.kind} sx={{ mt: 0.5 }}>
              <strong>{item.label}</strong>: Outstanding required fields —{" "}
              {item.missing.map((f) => f.replace(/_/g, " ")).join(", ")}.
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          mt: 2,
          pt: 1.75,
          borderTop: `1px solid ${IPD_COLORS.border}`,
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {allowed.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
            This surgical case episode has been concluded and closed.
          </Typography>
        ) : (
          <>
            {goMoves.map((to, index) => (
              <Button
                key={to}
                size="small"
                // The first legal move is the one nearly always taken, so it is
                // the one that looks like a button rather than a link.
                variant={index === 0 ? "contained" : "outlined"}
                onClick={() => onAdvance(to)}
              >
                {LIFECYCLE_LABELS[to] || to}
              </Button>
            ))}
            {stopMoves.length > 0 && (
              <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                {stopMoves.map((to) => (
                  <Button key={to} size="small" color="error" onClick={() => onAdvance(to)}>
                    {LIFECYCLE_LABELS[to] || to}
                  </Button>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* The reason stays on the case after it is rescheduled; it is only
          news while the case is still stopped. */}
      {((otCase.lifecycle_status === "CANCELLED" && otCase.cancel_reason) ||
        (otCase.lifecycle_status === "POSTPONED" && otCase.postpone_reason)) && (
        <Typography sx={{ mt: 1.5, fontSize: 12.5, color: IPD_COLORS.error.text }}>
          {otCase.lifecycle_status === "CANCELLED"
            ? `Cancelled: ${otCase.cancel_reason}`
            : `Postponed: ${otCase.postpone_reason}`}
          {otCase.cancel_category
            ? ` · ${otCase.cancel_category.replace(/_/g, " ").toLowerCase()}`
            : ""}
          {otCase.cancel_avoidable === true ? " · Audit: Avoidable" : otCase.cancel_avoidable === false ? " · Audit: Unavoidable" : ""}
        </Typography>
      )}
    </IpdSectionCard>
  );
}

export default function OtCaseDetail() {
  const { id } = useParams();
  const history = useHistory();
  const [tab, setTab] = useState("overview");
  const [otCase, setOtCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [move, setMove] = useState(null); // { to } while the reason dialog is open
  // The cancellation taxonomy, served by the lifecycle endpoint so the form
  // and the rule it has to satisfy come from the same place.
  const [categories, setCategories] = useState([]);

  const load = useCallback(async () => {
    setError("");
    try {
      setOtCase(await fetchCase(id));
    } catch (err) {
      setError(errorMessage(err, "Could not load this case"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const advance = async (to, withReason, extra) => {
    const res = await moveLifecycle(id, to, withReason, extra);
    if (report(res)) {
      setOtCase(res.data);
      setMove(null);
    }
  };

  const onAdvanceClick = async (to) => {
    if (!REASON_REQUIRED_FOR.includes(to)) {
      advance(to);
      return;
    }
    // Fetched when the dialog opens rather than on every page load: the
    // taxonomy is only needed by the one person stopping the one case, and
    // it is small enough that fetching it late costs nothing visible.
    try {
      const lifecycle = await fetchLifecycle(id);
      setCategories(lifecycle?.cancel_categories || []);
    } catch (err) {
      // The dialog still works without it — the server will refuse a
      // cancellation with no category and say so, which is a worse
      // experience than the dropdown but not a broken one.
      setCategories([]);
    }
    setMove({ to });
  };

  const allowed = otCase?.allowed_transitions || [];

  return (
    <OtScreen
      eyebrow="Operation Theatre"
      title={otCase ? `${otCase.case_number || "Case"} — ${otCase.patient_name || ""}` : "Case"}
      description={otCase?.diagnosis || ""}
      loading={loading}
      error={error}
      onRetry={load}
      actions={
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push(`${OT_BASE}/cases`)}>
          Back to Cases
        </Button>
      }
    >
      {otCase && (
        <>
          <CaseHeader
            otCase={otCase}
            allowed={allowed}
            onAdvance={onAdvanceClick}
          />

          <IpdTabBar
            tabs={TABS}
            activeTab={tab}
            onChange={setTab}
            ariaLabel="Stages of this OT case"
          />

          <Box sx={{ display: tab === "overview" ? "block" : "none" }}>
            <OverviewPanel otCase={otCase} onChanged={load} />
          </Box>
          <Box sx={{ display: tab === "preop" ? "block" : "none" }}>
            <PreopPanel otCase={otCase} onChanged={load} active={tab === "preop"} />
          </Box>
          <Box sx={{ display: tab === "flow" ? "block" : "none" }}>
            <FlowPanel otCase={otCase} onChanged={load} active={tab === "flow"} />
          </Box>
          <Box sx={{ display: tab === "theatre" ? "block" : "none" }}>
            <TheatrePanel otCase={otCase} onChanged={load} active={tab === "theatre"} />
          </Box>
          <Box sx={{ display: tab === "notes" ? "block" : "none" }}>
            <NotesPanel otCase={otCase} onChanged={load} />
          </Box>
          <Box sx={{ display: tab === "recovery" ? "block" : "none" }}>
            <RecoveryPanel otCase={otCase} onChanged={load} active={tab === "recovery"} />
          </Box>
          <Box sx={{ display: tab === "billing" ? "block" : "none" }}>
            <BillingPanel otCase={otCase} onChanged={load} active={tab === "billing"} />
          </Box>

          <StopCaseDialog
            open={Boolean(move)}
            target={move?.to}
            categories={categories}
            onConfirm={({ reason, ...extra }) => advance(move.to, reason, extra)}
            onCancel={() => setMove(null)}
          />
        </>
      )}
    </OtScreen>
  );
}
